import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Alert,
  Pagination as MuiPagination,
  IconButton,
  Divider,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { CardGridSkeleton } from '../components/shared/skeletons';
import { useSearchController } from './context/search-controller';
import { useSearchState } from './context/search-context';

export function SearchComponent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { performSearch } = useSearchController();
  const { results, totalRecords, pageCount, totalByType, searchTerm, loading, error } = useSearchState();

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<{
    term: string;
    type: string;
  }>({
    term: '',
    type: '',
  });

  const queryTerm = searchParams.get('q') || '';
  const queryType = searchParams.get('type') || '';

  // Helper function to get type label
  const getTypeLabel = (type: string) => {
    const labelMap: { [key: string]: string } = {
      'News': 'Notícias',
      'OfficialDaily': 'Diário Oficial',
      'Publication': 'Publicações',
    };
    return labelMap[type] || type;
  };

  // Opções de tipo de conteúdo
  const typeOptions = Object.entries(totalByType || {}).map(([type, count]) => ({
    value: type,
    label: `${getTypeLabel(type)} (${count})`,
  }));

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Termo de Busca',
      type: 'text',
      placeholder: 'Digite pelo menos 3 caracteres para buscar...',
      fullWidth: false,
      gridSize: { xs: 12, md: 6 },
    },
    {
      name: 'type',
      label: 'Tipo de Conteúdo',
      type: 'select',
      options: typeOptions,
      gridSize: { xs: 12, md: 6 },
    },
  ];

  useEffect(() => {
    if (queryTerm) {
      setFilters({
        term: queryTerm,
        type: queryType,
      });
      performSearch({
        Term: queryTerm,
        Types: queryType ? [queryType] : undefined,
        Page: 0,
        PageSize: 12,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTerm, queryType]);

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    if (filters.term.trim().length >= 3) {
      const params = new URLSearchParams();
      params.set('q', filters.term.trim());
      if (filters.type) {
        params.set('type', filters.type);
      }
      navigate(`/busca?${params.toString()}`);
      setCurrentPage(1);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      term: '',
      type: '',
    });
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    performSearch({
      Term: queryTerm,
      Types: filters.type ? [filters.type] : undefined,
      Page: page,
      PageSize: 12,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getTypeIcon = (type: string) => {
    const typeMap: { [key: string]: JSX.Element } = {
      'News': <NewspaperIcon />,
      'OfficialDaily': <ArticleIcon />,
      'Publication': <DescriptionIcon />,
      'Default': <ArticleIcon />,
    };
    return typeMap[type] || typeMap['Default'];
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleResultClick = (result: any) => {
    if (result.detailUrl) {
      navigate(result.detailUrl);
    }
  };

  return (
    <PageContainer>
      <Container maxWidth="xl">
        <PageHeader
          title="Busca no Portal"
          subtitle={searchTerm ? `Resultados para: "${searchTerm}"` : 'Pesquise por conteúdos no portal'}
          breadcrumbs={[
            { label: 'Busca no Portal' },
          ]}
        />

        {/* Search Filters */}
        <Box sx={{ mb: 3 }}>
          <ListFilters
            fields={filterFields.map((field) => ({
              ...field,
              value: filters[field.name as keyof typeof filters],
            }))}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onClear={handleClearFilters}
            isLoading={loading}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Results */}
        {loading && results.length === 0 ? (
          <CardGridSkeleton items={12} hasImage={false} hasActions={false} />
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : !searchTerm ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Digite um termo de busca e clique em "Buscar" para pesquisar no portal.
          </Alert>
        ) : results.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum resultado encontrado para "{searchTerm}".
            {filters.type && ' Tente remover o filtro de tipo.'}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {totalRecords} resultado{totalRecords !== 1 ? 's' : ''} encontrado{totalRecords !== 1 ? 's' : ''}
            </Typography>

            <Box sx={{ position: 'relative' }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1000,
                    borderRadius: 2,
                  }}
                >
                  <CardGridSkeleton items={6} hasImage={false} hasActions={false} />
                </Box>
              )}

              <Grid
                container
                spacing={2}
                sx={{
                  opacity: loading ? 0.5 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                {results.map((result) => (
                  <Grid item xs={12} key={`${result.type}-${result.id}`}>
                    <Card
                      elevation={2}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        },
                      }}
                      onClick={() => handleResultClick(result)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              flexShrink: 0,
                            }}
                          >
                            {getTypeIcon(result.type)}
                          </Box>

                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip
                                label={getTypeLabel(result.type)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {result.category && (
                                <Chip
                                  label={result.category}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>

                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: 'primary.main',
                              }}
                            >
                              {result.title}
                            </Typography>

                            {result.excerpt && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mb: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {result.excerpt}
                              </Typography>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {result.date && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(result.date)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          <IconButton
                            size="small"
                            color="primary"
                            sx={{
                              alignSelf: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <ArrowForwardIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {pageCount > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 4,
                  mb: 2,
                }}
              >
                <MuiPagination
                  count={pageCount}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  disabled={loading}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  );
}
